import type {Meta, StoryObj} from "@storybook/web-components";
import type {NotificationSettings} from "../../src/components/notification-settings/notification-settings";
import {html} from "lit";

//! these should be html-elements instead of strings
//! string controls output string (duh) that can be used like ${unsafeHTML(greeter_headline)}, see https://lit.dev/docs/templates/directives/#unsafehtml

type SlotArgs = {
	greeter_headline: string;
	greeter_text: string;

	channels_categories_headline: string;
	channels_categories_text: string;

	category_table_heading: string;
	notification_channel_table_heading: string;
	email_channel_table_heading: string;

	assets_headline: string;
	assets_text: string;

	assets_table_heading: string;

	save_button_label: string;
	cancel_button_label: string;

	cancel_modal_headline: string;
	cancel_confirm_label: string;
	cancel_reject_label: string;
};

const meta: Meta<NotificationSettings & SlotArgs> = {
	title: "Notification/Notification Settings",
	component: "uxn-notification-settings",

	decorators: [
		(story) =>
			// spell-checker: disable
			html` <fragment>
				<ux-u-silent-auth
					issuer="https://identity-s.trumpf.com"
					client-id="zyffE9ozijzUpQqdaOyteGMJfhMa"
					client-name="notificationmanagement"
					scope="openid"
					debug="true"
				></ux-u-silent-auth>
				${story()}
			</fragment>`,
		// spell-checker: enable
	],

	render: ({
		notificationsTooltip,
		emailTooltip,
		locale,
		env,
		version,
		formAction,
		formId,

		greeter_headline,
		greeter_text,

		channels_categories_headline,
		channels_categories_text,

		category_table_heading,
		notification_channel_table_heading,
		email_channel_table_heading,

		assets_headline,
		assets_text,

		assets_table_heading,

		save_button_label,
		cancel_button_label,

		cancel_modal_headline,
		cancel_confirm_label,
		cancel_reject_label,
	}) => html`
		<uxn-notification-settings
			notifications-tooltip="${notificationsTooltip}"
			email-tooltip="${emailTooltip}"
			locale="${locale}"
			env="${env}"
			version="${version}"
			form-action="${formAction}"
			form-id="${formId}"
		>
			<div class="headline-big" slot="greeter_headline">${greeter_headline}</div>
			<p slot="greeter_text">${greeter_text}</p>

			<div class="headline-big" slot="channels_categories_headline">${channels_categories_headline}</div>
			<p slot="channels_categories_text">${channels_categories_text}</p>

			<span slot="category_table_heading">${category_table_heading}</span>
			<span slot="notification_channel_table_heading">${notification_channel_table_heading}</span>
			<span slot="email_channel_table_heading">${email_channel_table_heading}</span>

			<div class="headline-big" slot="assets_headline">${assets_headline}</div>
			<p slot="assets_text">${assets_text}</p>

			<span slot="assets_table_heading">${assets_table_heading}</span>

			<span slot="save_button_label">${save_button_label}</span>
			<span slot="cancel_button_label">${cancel_button_label}</span>

			<h2 class="ux-text-headline ux-text-headline--medium" slot="cancel_modal_headline">${cancel_modal_headline}</h2>
			<span slot="cancel_confirm_label">${cancel_confirm_label}</span>
			<span slot="cancel_reject_label">${cancel_reject_label}</span>
		</uxn-notification-settings>
	`,

	argTypes: {
		notificationsTooltip: {control: "text"},
		emailTooltip: {control: "text"},
		locale: {control: "text"},
		env: {control: "select", options: ["prod", "qa", "dev"]}, //! are those really the available variants?
		version: {control: "select", options: ["2.0", "1.0"]}, //! are those really the available variants?
		formAction: {control: "text"},
		formId: {control: "text"},

		//! these should be {summary: "html-elements"} instead of strings
		greeter_headline: {
			name: '<slot name = "greeter_headline" />',
			description: "a custom topic headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		greeter_text: {
			name: '<slot name = "greeter_text" />',
			description: "a custom topic text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		channels_categories_headline: {
			name: '<slot name = "channels_categories_headline" />',
			description: "channels/categories headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		channels_categories_text: {
			name: '<slot name = "channels_categories_text" />',
			description: "channels/categories text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		category_table_heading: {
			name: '<slot name = "category_table_heading" />',
			description: "table heading",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		notification_channel_table_heading: {
			name: '<slot name = "notification_channel_table_heading" />',
			description: "channel column header",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		email_channel_table_heading: {
			name: '<slot name = "email_channel_table_heading" />',
			description: "email column header",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		assets_headline: {
			name: '<slot name = "assets_headline" />',
			description: "assets headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		assets_text: {
			name: '<slot name = "assets_text" />',
			description: "assets text",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		assets_table_heading: {
			name: '<slot name = "assets_table_heading" />',
			description: "assets table heading",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		save_button_label: {
			name: '<slot name = "save_button_label" />',
			description: "save-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		cancel_button_label: {
			name: '<slot name = "cancel_button_label" />',
			description: "cancel-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},

		cancel_modal_headline: {
			name: '<slot name = "cancel_modal_headline" />',
			description: "cancel-dialog headline",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		cancel_confirm_label: {
			name: '<slot name = "cancel_confirm_label" />',
			description: "cancel-confirm-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
		cancel_reject_label: {
			name: '<slot name = "cancel_reject_label" />',
			description: "cancel-reject-button label",
			table: {category: "Slots", type: {summary: "string"}},
			control: "text",
		},
	},
};

export default meta;
type Story = StoryObj<NotificationSettings & SlotArgs>;

export const Default: Story = {
	args: {
		notificationsCenterUrl: "",
		notificationsTooltip: "With the checkbox activated, you will receive notifications in MyTRUMPF.",
		locale: "en_INT",
		emailTooltip:
			"With the checkbox activated, you will receive notifications via email. Please note that e-mail notifications here include your individual and transactional notifications. You can unsubscribe from TRUMPF newsletters here.",
		version: "2.0",
		env: "qa",
		formAction: "",
		formId: "",

		greeter_headline: "Notification Settings",
		greeter_text: "In this tab you can configure your notification settings.",

		channels_categories_headline: "Channels/categories",
		channels_categories_text: "Select below how you want to receive notifications.",

		category_table_heading: "Category",
		notification_channel_table_heading: "Notification",
		email_channel_table_heading: "Email",

		assets_headline: "Choose assets",
		assets_text:
			"Consectetur adipisicing magna deserunt minim proident ad. Duis sunt deserunt voluptate qui sunt voluptate aliquip tempor. Esse aliqua voluptate nulla anim. Ad eiusmod enim esse sunt. Quis laboris sunt aliquip aliqua aliqua nisi labore laborum nisi id.",

		assets_table_heading: "My Trumpf machines",

		save_button_label: "Save changes",
		cancel_button_label: "Discard changes",

		cancel_modal_headline: "Are you sure you want to discard changes?",
		cancel_confirm_label: "Discard changes",
		cancel_reject_label: "Continue editing",
	},
};
